import { CopyIcon, UpDownIcon } from '@chakra-ui/icons'
import {
  AspectRatio as ChakraAspectRatio,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  useClipboard,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import moment from 'moment'
import numeral from 'numeral'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineDeleteRow, AiOutlinePlusCircle } from 'react-icons/ai'
import { BsChevronDown, BsImageFill, BsTags } from 'react-icons/bs'
import { HiOutlineDocumentDownload } from 'react-icons/hi'
import { LuHistory } from 'react-icons/lu'
import {
  MdCenterFocusWeak,
  MdOutlineRestore,
  MdOutlineShare,
} from 'react-icons/md'
import { NavLink, useHistory, useRouteMatch } from 'react-router-dom'
import styled from 'styled-components'
import useFetch, { CachePolicies } from 'use-http'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils'
import { FeaturesEnum } from '../../../../utils/features'
import {
  canAddTags,
  downloadFileFromUrl,
  getFileURL,
  isEditableImage,
  mediaLibraryThumbnailTransformation,
} from '../../../../utils/mediaLibrary'
import {
  removeLeadingSlash,
  removeTrailingSlash,
} from '../../../../utils/urlHelpers'
import { ACLPermissionEnum } from '../../../constants'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import useToast from '../../../hooks/useToast'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import type { MLFileDetailRouteParams } from '../../../layouts/MediaLibrary/FileDetailBaseLayout'
import { UserContext } from '../../../providers'
import { MediaLibraryActions, MLContext } from '../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import PermissionGuard from '../../PermissionGuard'
import CopyEmbedURLModal from '../CopyEmbedURLModal'
import FilePreview from '../FilePreview'
import FileUpload from '../NewButton/FileUpload'
import PhotoEditor from '../PhotoEditor'
import FileHistoryDrawer from '../MediaOperations/FileHistoryDrawer'
import CustomFocusAreaModal from './CustomFoucsAreaModal'
import DeleteFileVersionModal from './DeleteFileVersionModal'
import RestoreFileVersionModal from './RestoreFileVersionModal'

const AspectRatio = styled(ChakraAspectRatio)`
  & > img,
  & > video {
    object-fit: contain !important;
  }
`

const FileDetailHeaderToolbar = ({
  fileDetails,
  loading,
  toggleTagsModal,
  getFileDetails,
  onOpenShareModal,
}: {
  fileDetails: FileDetailsResponse
  loading: boolean
  toggleTagsModal(isTagsModalOpen: boolean): void
  getFileDetails(): Promise<FileDetailsResponse>
  onOpenShareModal(): void
}): React.ReactElement => {
  const {
    isPublicLink,
    urlPrefix,
    clientInfo,
    dashboardServerURL: publicLinkDashboardServerURL,
  } = useContext(PublicLinksInfoContext)
  const [, dispatch] = useContext(MLContext)
  const { user } = useContext(UserContext)
  const dashboardServerURL =
    user?.dashboardServerURL ?? publicLinkDashboardServerURL
  const history = useHistory()

  const {
    params: { fileId, versionId },
  } = useRouteMatch<MLFileDetailRouteParams>()

  const { t } = useTranslation()
  const [isFocusAreaModalOpen, toggleFocusAreaModal] = useState(false)
  const [isFileUploadModalOpen, openFileUploadModal] = useState(false)
  const toast = useToast()
  const [openEmbedModal, handleEmbedModal] = useState(false)
  const [urlToCopy, setUrlToCopy] = useState<string>('')
  const [isToggled, setIsToggled] = useState<boolean>(false)
  const copyUrl = useRef<boolean>(false)
  const { onCopy } = useClipboard(urlToCopy)
  const { request } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    if (copyUrl.current) {
      onCopy()
      toast({
        description: t('copied_url_to_clipboard', 'Copied URL to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      copyUrl.current = false
    }
  }, [isToggled])

  const downloadFile = (): void => {
    const downloadURL = `${removeTrailingSlash(
      dashboardServerURL!,
    )}/${removeLeadingSlash(urlPrefix)}api/v2/files/${
      fileDetails.fileId
    }/download?fileName=${fileDetails.name}`
    downloadFileFromUrl(downloadURL)
  }

  const {
    isOpen: isOpenPhotoEditor,
    onOpen: onOpenPhotoEditor,
    onClose: onClosePhotoEditor,
  } = useDisclosure()

  const {
    isOpen: isOpenRestoreFileVersion,
    onOpen: onOpenRestoreFileVersion,
    onClose: onCloseRestoreFileVersion,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteFileVersion,
    onOpen: onOpenDeleteFileVersion,
    onClose: onCloseDeleteFileVersion,
  } = useDisclosure()

  const {
    isOpen: isOpenHistoryDrawer,
    onOpen: onOpenHistoryDrawer,
    onClose: onCloseHistoryDrawer,
  } = useDisclosure()

  const {
    data: versions,
    get: refetchFileVersions,
    loading: loadingVersions,
  } = useFetch<FileDetailsResponse[]>(
    `/api/v2/files/${fileDetails.fileId}/versions`,
    { method: 'GET', data: [], cachePolicy: CachePolicies.NO_CACHE },
  )

  useEffect(() => {
    if (!isPublicLink && Array.isArray(versions)) {
      dispatch({
        type: MediaLibraryActions.SET_DETAIL_PAGE_VERSIONS,
        payload: versions,
      })
    }
  }, [versions])

  useEffect(() => {
    if (!isPublicLink) {
      void refetchFileVersions()
    }
  }, [fileDetails, isPublicLink])

  const files = useMemo(
    () => versions?.filter(x => x.type === 'file'),
    [versions],
  )

  const fileVersions = useMemo(
    () => versions?.filter(x => x.type === 'file-version'),
    [versions],
  )

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        height="100%"
        lineHeight={1.5}
        justifyContent="space-between"
      >
        <Flex
          alignItems="flex-start"
          justifyContent="center"
          direction="column"
        >
          <Flex alignItems="center">
            <Box maxWidth="18em">
              <Tooltip label={fileDetails.name}>
                <Text isTruncated fontWeight="semibold">
                  {fileDetails.name}
                </Text>
              </Tooltip>
            </Box>
            <Tooltip label={`${t('copy_url', 'Copy URL')}`}>
              <CopyIcon
                cursor="pointer"
                marginLeft="5px"
                onClick={() => {
                  copyUrl.current = true
                  void getFileURL({
                    file: fileDetails,
                    request,
                    ...getFileUrlDynamicArguments,
                  })
                    .then(data => {
                      if (data.includes('ik-s=')) {
                        if (data.includes('ik-auth-version')) {
                          const url = new URL(data)
                          url.searchParams.delete('ik-auth-version')
                          url.searchParams.delete('ik-s')
                          url.searchParams.delete('ik-t')
                          setUrlToCopy(url.toString())
                        } else {
                          setUrlToCopy(data)
                        }
                        setIsToggled(prev => !prev)
                      } else {
                        setUrlToCopy(data)
                        setIsToggled(prev => !prev)
                      }
                    })
                    .catch(() => {
                      toast({
                        description: t(
                          'generating_url_to_copy_error',
                          'There was an error in generating the URL',
                        ),
                        status: 'error',
                        isClosable: true,
                        duration: 5000,
                        position: 'top-right',
                        variant: 'outline',
                      })
                    })
                }}
              />
            </Tooltip>
          </Flex>

          {!isPublicLink ? (
            <Flex>
              <Box maxWidth="20em">
                <Popover placement="bottom-start">
                  <PopoverTrigger>
                    <Button
                      variant="link"
                      fontWeight="light"
                      fontSize="smaller"
                      rightIcon={<BsChevronDown />}
                      isLoading={loadingVersions}
                    >
                      {fileDetails.type === 'file'
                        ? 'Latest version'
                        : fileDetails.versionInfo.name}{' '}
                      ({fileDetails.versionInfo.id.slice(-6)})
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    borderRadius="4px"
                    padding="0px"
                    _focus={{ boxShadow: 'none' }}
                  >
                    <PopoverArrow />
                    <PopoverBody padding="0px">
                      {loadingVersions ? (
                        <Center>
                          <Spinner />
                        </Center>
                      ) : (
                        <VStack
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          width="full"
                          spacing="2"
                          paddingX="4"
                          paddingY="4"
                          overflow="scroll"
                          maxHeight="320px"
                        >
                          {/* merge files and fileVersions and change to createdAt */}
                          {files?.map((version, index) => {
                            const isCurrentVersion =
                              version.type === 'file' && !versionId
                            return (
                              <Flex
                                key={version.versionInfo.id}
                                direction="column"
                                width="full"
                                as={NavLink}
                                to={`/media-library/detail/${version.fileId}`}
                                borderRadius="md"
                                padding="2"
                                outline="1px solid"
                                background={
                                  isCurrentVersion ? 'transparent' : '#f7fafc'
                                }
                                outlineColor={
                                  isCurrentVersion ? 'appClearBlue' : '#e1e4eb'
                                }
                              >
                                <Flex align="center" justify="space-between">
                                  <Flex direction="column">
                                    <Text
                                      fontWeight="semibold"
                                      color={
                                        isCurrentVersion
                                          ? 'appClearBlue'
                                          : 'black'
                                      }
                                      isTruncated
                                    >
                                      Current version
                                    </Text>
                                    <Text color="appSlate" fontSize="sm">
                                      <Tooltip
                                        label={moment(
                                          version.updatedAt,
                                        ).toLocaleString()}
                                        portalProps={{
                                          appendToParentPortal: true,
                                        }}
                                      >
                                        {moment(version.updatedAt).fromNow()}
                                      </Tooltip>
                                      {' • '}
                                      {numeral(version.size)
                                        .format('0.00 ib')
                                        .replace('i', '')}
                                    </Text>
                                  </Flex>
                                  <AspectRatio width="30%" ratio={1 / 1}>
                                    <FilePreview
                                      file={version}
                                      folderPath={version.filePath}
                                      transformation={
                                        mediaLibraryThumbnailTransformation
                                      }
                                      disablePadding={true}
                                    />
                                  </AspectRatio>
                                </Flex>
                              </Flex>
                            )
                          })}
                          {fileVersions?.map((version, index) => {
                            const isCurrentVersion =
                              version.versionInfo.id === versionId

                            const isNextCurrentVersion =
                              fileVersions[index + 1]?.versionInfo.id ===
                              versionId

                            const currentVersionStyles = isCurrentVersion
                              ? {
                                  outline: '1px solid',
                                  outlineColor: 'appClearBlue',
                                  padding: '2',
                                }
                              : {
                                  paddingX: '2',
                                  outline: '1px solid',
                                  outlineColor: 'transparent',
                                  paddingTop: index === 0 ? '2' : '0',
                                }
                            return (
                              <Flex
                                key={version.versionInfo.id}
                                direction="column"
                                width="full"
                                as={NavLink}
                                to={`/media-library/detail/${version.fileId}/${version.versionInfo.id}`}
                                borderRadius="md"
                                {...currentVersionStyles}
                              >
                                <Flex align="center" justify="space-between">
                                  <Flex direction="column">
                                    <Text
                                      fontWeight="semibold"
                                      color={
                                        isCurrentVersion
                                          ? 'appClearBlue'
                                          : 'black'
                                      }
                                      isTruncated
                                    >
                                      {version.versionInfo.name}
                                    </Text>

                                    <Text color="appSlate" fontSize="sm">
                                      <Tooltip
                                        label={moment(
                                          version.createdAt,
                                        ).toLocaleString()}
                                        portalProps={{
                                          appendToParentPortal: true,
                                        }}
                                      >
                                        {moment(version.createdAt).fromNow()}
                                      </Tooltip>
                                      {' • '}
                                      {numeral(version.size)
                                        .format('0.00 ib')
                                        .replace('i', '')}
                                    </Text>
                                  </Flex>
                                  <AspectRatio width="30%" ratio={1 / 1}>
                                    <FilePreview
                                      file={version}
                                      folderPath={version.filePath}
                                      transformation={
                                        mediaLibraryThumbnailTransformation
                                      }
                                      disablePadding={true}
                                    />
                                  </AspectRatio>
                                </Flex>

                                <Divider
                                  borderColor={
                                    !isCurrentVersion &&
                                    !isNextCurrentVersion &&
                                    index !== fileVersions.length - 1
                                      ? 'inherit'
                                      : 'transparent'
                                  }
                                  marginTop={
                                    !isCurrentVersion &&
                                    !isNextCurrentVersion &&
                                    index !== fileVersions.length - 1
                                      ? '2'
                                      : '0'
                                  }
                                />
                              </Flex>
                            )
                          })}
                        </VStack>
                      )}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </Box>
              {checkOperationPermission({
                user,
                aclPermission: [
                  ACLPermissionEnum.CONTRIBUTE,
                  ACLPermissionEnum.MANAGE,
                ],
                selectedFiles: [fileDetails],
                selectedFolders: [],
                selectedMediaCollections: [],
              }) && (
                <Tooltip
                  label={`${t(
                    'upload_new_file_version',
                    'Upload new file version',
                  )}`}
                  shouldWrapChildren
                >
                  <Icon
                    as={AiOutlinePlusCircle}
                    cursor="pointer"
                    marginTop="3px"
                    marginLeft="5px"
                    onClick={() => {
                      openFileUploadModal(true)
                    }}
                  />
                </Tooltip>
              )}
            </Flex>
          ) : null}
        </Flex>
        <Box>
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission && fileDetails && !loading ? (
                <HStack spacing="18px">
                  {!isPublicLink &&
                  fileDetails.type === 'file' &&
                  user?.pricingPlan.featuresList.includes(
                    FeaturesEnum.ACL_AND_COLLECTIONS,
                  ) &&
                  checkOperationPermission({
                    user,
                    aclPermission: [ACLPermissionEnum.MANAGE],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => {
                        onOpenShareModal()
                      }}
                    >
                      <MdOutlineShare />
                      <Text marginLeft="5px">{t('share', 'Share')}</Text>
                    </Flex>
                  ) : null}
                  {fileDetails.mime?.includes('video') &&
                    fileDetails.isPublished &&
                    isPublicLink !== true && (
                      <Flex
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => {
                          handleEmbedModal(true)
                        }}
                      >
                        <UpDownIcon />
                        <Text marginLeft="5px">{t('embed', 'Embed')}</Text>
                        {openEmbedModal && (
                          <CopyEmbedURLModal
                            file={fileDetails}
                            openEmbedModal={openEmbedModal}
                            handleEmbedModal={handleEmbedModal}
                          />
                        )}
                      </Flex>
                    )}
                  {!isPublicLink &&
                  canAddTags(fileDetails) &&
                  checkOperationPermission({
                    user,
                    aclPermission: [
                      ACLPermissionEnum.CONTRIBUTE,
                      ACLPermissionEnum.MANAGE,
                    ],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => void toggleTagsModal(true)}
                    >
                      <BsTags />
                      <Text marginLeft="5px">{t('tags', 'Tags')}</Text>
                    </Flex>
                  ) : null}
                  {!isPublicLink &&
                  isEditableImage(fileDetails) &&
                  checkOperationPermission({
                    user,
                    aclPermission: [
                      ACLPermissionEnum.CONTRIBUTE,
                      ACLPermissionEnum.MANAGE,
                    ],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <React.Fragment>
                      <Flex
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => void toggleFocusAreaModal(true)}
                      >
                        <MdCenterFocusWeak />
                        <Text marginLeft="5px">
                          {t('edit_foucs_area', 'Edit Focus Area')}
                        </Text>
                      </Flex>
                      <Flex
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => void onOpenPhotoEditor()}
                      >
                        <BsImageFill />
                        <Text marginLeft="5px">
                          {t('edit_image', 'Edit Image')}
                        </Text>
                      </Flex>
                    </React.Fragment>
                  ) : null}
                  {fileDetails.type === 'file' &&
                  checkOperationPermission({
                    user,
                    aclPermission: [
                      ACLPermissionEnum.READ,
                      ACLPermissionEnum.CONTRIBUTE,
                      ACLPermissionEnum.MANAGE,
                    ],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => void downloadFile()}
                    >
                      <HiOutlineDocumentDownload />
                      <Text marginLeft="5px">{t('download', 'Download')}</Text>
                    </Flex>
                  ) : null}
                  {fileDetails.type === 'file' &&
                  user?.pricingPlan.featuresList.includes(
                    FeaturesEnum.CENTRAL_AUDIT_LOGS,
                  ) &&
                  checkOperationPermission({
                    user,
                    aclPermission: [
                      ACLPermissionEnum.READ,
                      ACLPermissionEnum.CONTRIBUTE,
                      ACLPermissionEnum.MANAGE,
                    ],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => void onOpenHistoryDrawer()}
                    >
                      <LuHistory />
                      <Text marginLeft="5px">{t('history', 'History')}</Text>
                    </Flex>
                  ) : (
                    <></>
                  )}
                  {fileDetails.type === 'file-version' &&
                  checkOperationPermission({
                    user,
                    aclPermission: [
                      ACLPermissionEnum.CONTRIBUTE,
                      ACLPermissionEnum.MANAGE,
                    ],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => void onOpenRestoreFileVersion()}
                    >
                      <MdOutlineRestore />
                      <Text marginLeft="5px">
                        {t('restore_version', 'Restore Version')}
                      </Text>
                    </Flex>
                  ) : null}
                  {fileDetails.type === 'file-version' &&
                  checkOperationPermission({
                    user,
                    aclPermission: [ACLPermissionEnum.MANAGE],
                    selectedFiles: [fileDetails],
                    selectedFolders: [],
                    selectedMediaCollections: [],
                  }) ? (
                    <Flex
                      alignItems="center"
                      cursor="pointer"
                      onClick={() => void onOpenDeleteFileVersion()}
                    >
                      <AiOutlineDeleteRow />
                      <Text marginLeft="5px">
                        {t('delete_version', 'Delete Version')}
                      </Text>
                    </Flex>
                  ) : null}
                </HStack>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Box>

        {isEditableImage(fileDetails) && (
          <CustomFocusAreaModal
            fileDetails={fileDetails}
            isOpen={isFocusAreaModalOpen}
            onClose={() => {
              toggleFocusAreaModal(false)
            }}
            getFileDetails={getFileDetails}
          />
        )}
        {isEditableImage(fileDetails) && isOpenPhotoEditor && (
          <PhotoEditor onClose={onClosePhotoEditor} fileDetails={fileDetails} />
        )}
        {fileDetails.type === 'file-version' ? (
          <>
            <RestoreFileVersionModal
              onSuccessClose={async () => {
                await refetchFileVersions()
                history.push(`/media-library/detail/${fileId}`)
              }}
              isOpen={isOpenRestoreFileVersion}
              onClose={onCloseRestoreFileVersion}
            />
            <DeleteFileVersionModal
              onSuccessClose={async () => {
                await refetchFileVersions()
                history.push(`/media-library/detail/${fileId}`)
              }}
              isOpen={isOpenDeleteFileVersion}
              onClose={onCloseDeleteFileVersion}
            />
          </>
        ) : null}
      </Flex>
      <FileHistoryDrawer
        isOpen={isOpenHistoryDrawer}
        onClose={onCloseHistoryDrawer}
        fileDetails={fileDetails}
      />
      <FileUpload
        isModalOpen={isFileUploadModalOpen}
        openModal={() => {
          openFileUploadModal(true)
        }}
        closeModal={async () => {
          openFileUploadModal(false)
          await refetchFileVersions()
          await getFileDetails()
        }}
        fileVersion={fileDetails}
      />
    </>
  )
}

export default FileDetailHeaderToolbar
